/**
 * Create Route Page
 * Form for creating new raw material transport routes with multi-stop configuration
 * Uses dropdowns for Manufacturing Units and Gaushalas
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Plus, X, CheckCircle } from 'lucide-react';
import {
  transportApi,
  type AddStopRequest,
  type CreateRouteRequest,
  type RouteStop,
  type ManufacturingUnitDTO,
  type GaushalaDTO,
  type ScheduleTypeDTO,
  type RawMaterialTypeDTO
} from '../../services/transport/api';

export default function CreateRoutePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [stops, setStops] = useState<Partial<RouteStop>[]>([]);

  // Dropdown data
  const [manufacturingUnits, setManufacturingUnits] = useState<ManufacturingUnitDTO[]>([]);
  const [gaushalas, setGaushalas] = useState<GaushalaDTO[]>([]);
  const [scheduleTypes, setScheduleTypes] = useState<ScheduleTypeDTO[]>([]);
  const [rawMaterialTypes, setRawMaterialTypes] = useState<RawMaterialTypeDTO[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    routeCode: '',
    routeName: '',
    manufacturingUnitId: '',
    mfgUnitAddress: '', // Address for the selected manufacturing unit
    scheduleTypeId: '', // Will be set when schedule types are loaded
    routeStartTime: '06:00',
    scheduleDate: '' // Date for Weekly/Custom/Manual schedule types
  });

  // Selected manufacturing unit (for auto-filling address)
  const [selectedMfgUnit, setSelectedMfgUnit] = useState<ManufacturingUnitDTO | null>(null);

  // Get selected schedule type for conditional date picker
  const selectedScheduleType = scheduleTypes.find(st => st.id.toString() === formData.scheduleTypeId);

  // Current stop being added
  const [selectedGaushala, setSelectedGaushala] = useState<GaushalaDTO | null>(null);
  const [currentStop, setCurrentStop] = useState({
    gaushalaId: '',
    sequenceOrder: 1,
    expectedQuantityMinKg: '',
    expectedQuantityMaxKg: '',
    rawMaterialTypeId: ''
  });

  // Load dropdown data on mount - each dropdown loads independently
  useEffect(() => {
    const loadDropdowns = async () => {
      setLoadingDropdowns(true);
      const failedDropdowns: string[] = [];

      // Use Promise.allSettled so one failure doesn't block others
      const [mfgResult, gaushalaResult, scheduleResult, rawMaterialResult] = await Promise.allSettled([
        transportApi.getManufacturingUnits(),
        transportApi.getGaushalas(),
        transportApi.getScheduleTypes(),
        transportApi.getRawMaterialTypes()
      ]);

      // Process each result independently
      if (mfgResult.status === 'fulfilled') {
        setManufacturingUnits(mfgResult.value);
      } else {
        console.error('Failed to load manufacturing units:', mfgResult.reason);
        failedDropdowns.push('Manufacturing Units');
      }

      if (gaushalaResult.status === 'fulfilled') {
        setGaushalas(gaushalaResult.value);
      } else {
        console.error('Failed to load gaushalas:', gaushalaResult.reason);
        failedDropdowns.push('Gaushalas');
      }

      if (scheduleResult.status === 'fulfilled') {
        const scheduleTypeList = scheduleResult.value;
        setScheduleTypes(scheduleTypeList);
        // Set default schedule type (DAILY) if available
        const dailyType = scheduleTypeList.find((st: ScheduleTypeDTO) => st.code === 'DAILY');
        if (dailyType) {
          setFormData(prev => ({ ...prev, scheduleTypeId: dailyType.id.toString() }));
        } else if (scheduleTypeList.length > 0) {
          setFormData(prev => ({ ...prev, scheduleTypeId: scheduleTypeList[0].id.toString() }));
        }
      } else {
        console.error('Failed to load schedule types:', scheduleResult.reason);
        failedDropdowns.push('Schedule Types');
      }

      if (rawMaterialResult.status === 'fulfilled') {
        setRawMaterialTypes(rawMaterialResult.value);
      } else {
        console.error('Failed to load raw material types:', rawMaterialResult.reason);
        failedDropdowns.push('Raw Material Types');
      }

      // Show specific error for failed dropdowns
      if (failedDropdowns.length > 0) {
        const isAuthError = [mfgResult, gaushalaResult, scheduleResult, rawMaterialResult]
          .some(r => r.status === 'rejected' && r.reason?.message?.includes('Session expired'));
        if (isAuthError) {
          setError('Session expired. Please login again to load dropdown data.');
        } else {
          setError(`Failed to load: ${failedDropdowns.join(', ')}. Please refresh the page.`);
        }
      }

      setLoadingDropdowns(false);
    };
    loadDropdowns();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // If manufacturing unit changed, update selected unit and auto-fill address
    if (name === 'manufacturingUnitId' && value) {
      const unit = manufacturingUnits.find(u => u.id === parseInt(value));
      setSelectedMfgUnit(unit || null);
      if (unit) {
        setFormData(prev => ({ ...prev, manufacturingUnitId: value, mfgUnitAddress: unit.address || '' }));
        return;
      }
    }
  };

  const handleGaushalaSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gaushalaId = e.target.value;
    setCurrentStop(prev => ({ ...prev, gaushalaId }));

    if (gaushalaId) {
      const gaushala = gaushalas.find(g => g.id === parseInt(gaushalaId));
      setSelectedGaushala(gaushala || null);
    } else {
      setSelectedGaushala(null);
    }
  };

  const handleStopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentStop((prev) => ({
      ...prev,
      [name]: name.includes('expectedQuantity') || name === 'sequenceOrder'
        ? parseFloat(value) || ''
        : value
    }));
  };

  const addStop = () => {
    if (!currentStop.gaushalaId || !selectedGaushala) {
      setError('Please select a gaushala');
      return;
    }

    if (!currentStop.rawMaterialTypeId) {
      setError('Please select a raw material type');
      return;
    }

    // Check for duplicate gaushala
    if (stops.some((s) => s.gaushalaId?.toString() === currentStop.gaushalaId)) {
      setError('This gaushala is already added to the route');
      return;
    }

    // Check for duplicate sequence order
    const seqOrder = currentStop.sequenceOrder || stops.length + 1;
    if (stops.some((s) => s.sequenceOrder === seqOrder)) {
      setError('Sequence order must be unique. Please use a different sequence number.');
      return;
    }

    const selectedRawMaterialType = currentStop.rawMaterialTypeId
      ? rawMaterialTypes.find(t => t.id.toString() === currentStop.rawMaterialTypeId)
      : null;

    setStops((prev) => [
      ...prev,
      {
        gaushalaId: parseInt(currentStop.gaushalaId),
        gaushalaName: selectedGaushala.gaushalaName,
        gaushalaAddress: selectedGaushala.registeredAddress,
        gaushalaLatitude: selectedGaushala.latitude,
        gaushalaLongitude: selectedGaushala.longitude,
        sequenceOrder: seqOrder,
        expectedQuantityMinKg: currentStop.expectedQuantityMinKg ? parseFloat(currentStop.expectedQuantityMinKg.toString()) : undefined,
        expectedQuantityMaxKg: currentStop.expectedQuantityMaxKg ? parseFloat(currentStop.expectedQuantityMaxKg.toString()) : undefined,
        rawMaterialTypeId: selectedRawMaterialType ? selectedRawMaterialType.id : undefined,
        rawMaterialTypeName: selectedRawMaterialType ? selectedRawMaterialType.name : undefined
      }
    ]);

    // Reset current stop
    setCurrentStop({
      gaushalaId: '',
      sequenceOrder: stops.length + 2,
      expectedQuantityMinKg: '',
      expectedQuantityMaxKg: '',
      rawMaterialTypeId: ''
    });
    setSelectedGaushala(null);
    setError(null);
  };

  const removeStop = (index: number) => {
    setStops((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.routeCode || !formData.routeName || !formData.manufacturingUnitId) {
      setError('Please fill in all required route fields');
      return;
    }

    // Validate date for Weekly/Custom/Manual schedule types
    const scheduleType = scheduleTypes.find(st => st.id.toString() === formData.scheduleTypeId);
    if (scheduleType && ['WEEKLY', 'CUSTOM', 'MANUAL'].includes(scheduleType.code) && !formData.scheduleDate) {
      setError('Please select a schedule date for this schedule type');
      return;
    }

    if (!selectedMfgUnit) {
      setError('Please select a manufacturing unit');
      return;
    }

    if (!formData.mfgUnitAddress.trim()) {
      setError('Manufacturing unit address is required. Please enter the address.');
      return;
    }

    if (stops.length === 0) {
      setError('Please add at least one gaushala stop');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create route first
      const createRouteRequest: CreateRouteRequest = {
        routeCode: formData.routeCode,
        routeName: formData.routeName,
        manufacturingUnitId: parseInt(formData.manufacturingUnitId),
        mfgUnitName: selectedMfgUnit.name,
        mfgUnitAddress: formData.mfgUnitAddress.trim(),
        mfgUnitLatitude: selectedMfgUnit.latitude,
        mfgUnitLongitude: selectedMfgUnit.longitude,
        scheduleTypeId: parseInt(formData.scheduleTypeId),
        scheduleConfig: JSON.stringify({
          time: formData.routeStartTime,
          ...(formData.scheduleDate && { date: formData.scheduleDate })
        })
      };

      const createdRoute = await transportApi.createRoute(createRouteRequest);

      // Add stops to the created route
      for (const stop of stops) {
        await transportApi.addStop(createdRoute.id!, {
          gaushalaId: stop.gaushalaId!,
          gaushalaName: stop.gaushalaName!,
          gaushalaAddress: stop.gaushalaAddress,
          gaushalaLatitude: stop.gaushalaLatitude,
          gaushalaLongitude: stop.gaushalaLongitude,
          sequenceOrder: stop.sequenceOrder!,
          expectedQuantityMinKg: stop.expectedQuantityMinKg,
          expectedQuantityMaxKg: stop.expectedQuantityMaxKg,
          rawMaterialTypeId: stop.rawMaterialTypeId
        } as AddStopRequest);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/transport/raw-material/routes/${createdRoute.id}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create route');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-600 mb-4" />
        <h2 className="text-lg font-semibold text-green-900 mb-2">Route Created Successfully</h2>
        <p className="text-sm text-green-800">Redirecting to route details...</p>
      </div>
    );
  }

  if (loadingDropdowns) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading form data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Create New Route</h2>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Route Details */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Route Details</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route Code *</label>
              <input
                type="text"
                name="routeCode"
                value={formData.routeCode}
                onChange={handleInputChange}
                placeholder="e.g., RMT-001"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route Name *</label>
              <input
                type="text"
                name="routeName"
                value={formData.routeName}
                onChange={handleInputChange}
                placeholder="e.g., Delhi Morning Route"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type *</label>
              <select
                name="scheduleTypeId"
                value={formData.scheduleTypeId}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              >
                {scheduleTypes.length === 0 && (
                  <option value="">Loading schedule types...</option>
                )}
                {scheduleTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route Start Time *</label>
              <input
                type="time"
                name="routeStartTime"
                value={formData.routeStartTime}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            {/* Conditional date picker for Weekly, Custom, and Manual schedule types */}
            {selectedScheduleType && ['WEEKLY', 'CUSTOM', 'MANUAL'].includes(selectedScheduleType.code) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Date *</label>
                <input
                  type="date"
                  name="scheduleDate"
                  value={formData.scheduleDate}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {selectedScheduleType.code === 'WEEKLY' && 'Select the date for this weekly pickup'}
                  {selectedScheduleType.code === 'CUSTOM' && 'Select the date for this custom schedule'}
                  {selectedScheduleType.code === 'MANUAL' && 'Select the date for this manual pickup'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Manufacturing Unit (Dropdown) */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Manufacturing Unit (Drop-off Point) *</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Manufacturing Unit</label>
              <select
                name="manufacturingUnitId"
                value={formData.manufacturingUnitId}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                required
              >
                <option value="">-- Select Manufacturing Unit --</option>
                {manufacturingUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Show selected unit details */}
            {selectedMfgUnit && (
              <div className="rounded-lg bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">Selected: {selectedMfgUnit.name}</p>
                <p className="text-sm text-blue-700">Code: {selectedMfgUnit.code}</p>
              </div>
            )}

            {/* Manufacturing Unit Address - editable field */}
            {selectedMfgUnit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturing Unit Address *</label>
                <input
                  type="text"
                  name="mfgUnitAddress"
                  value={formData.mfgUnitAddress}
                  onChange={handleInputChange}
                  placeholder="Enter manufacturing unit address"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
                {!formData.mfgUnitAddress && (
                  <p className="mt-1 text-xs text-amber-600">Address is required. Please enter the manufacturing unit address.</p>
                )}
              </div>
            )}

            {manufacturingUnits.length === 0 && (
              <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                No manufacturing units found. Please ensure manufacturing units are created in the system.
              </p>
            )}
          </div>
        </div>

        {/* Add Gaushala Stops (Dropdown) */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gaushala Stops (Pickup Points)</h3>

          {/* Current Stop Input */}
          <div className="mb-6 space-y-4 rounded-lg bg-blue-50 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Gaushala *</label>
                <select
                  value={currentStop.gaushalaId}
                  onChange={handleGaushalaSelect}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Select Gaushala --</option>
                  {gaushalas.map((gaushala) => (
                    <option
                      key={gaushala.id}
                      value={gaushala.id}
                      disabled={stops.some(s => s.gaushalaId === gaushala.id)}
                    >
                      {gaushala.gaushalaName} {stops.some(s => s.gaushalaId === gaushala.id) ? '(Already added)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Show selected gaushala details */}
              {selectedGaushala && (
                <div className="sm:col-span-2 rounded-lg bg-white p-3 border border-blue-200">
                  <p className="text-sm font-medium text-gray-900">{selectedGaushala.gaushalaName}</p>
                  {selectedGaushala.registeredAddress && (
                    <p className="text-sm text-gray-600">{selectedGaushala.registeredAddress}</p>
                  )}
                  {selectedGaushala.contactNumber && (
                    <p className="text-sm text-gray-600">Contact: {selectedGaushala.contactNumber}</p>
                  )}
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type of Raw Material *</label>
                <select
                  value={currentStop.rawMaterialTypeId}
                  onChange={(e) => setCurrentStop(prev => ({ ...prev, rawMaterialTypeId: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Select Raw Material Type --</option>
                  {rawMaterialTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Order *</label>
                <input
                  type="number"
                  name="sequenceOrder"
                  value={currentStop.sequenceOrder}
                  onChange={handleStopInputChange}
                  min="1"
                  placeholder="1, 2, 3..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Expected Qty (kg)</label>
                <input
                  type="number"
                  name="expectedQuantityMinKg"
                  value={currentStop.expectedQuantityMinKg}
                  onChange={handleStopInputChange}
                  placeholder="e.g., 100"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Expected Qty (kg)</label>
                <input
                  type="number"
                  name="expectedQuantityMaxKg"
                  value={currentStop.expectedQuantityMaxKg}
                  onChange={handleStopInputChange}
                  placeholder="e.g., 200"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={addStop}
              disabled={!currentStop.gaushalaId || !currentStop.rawMaterialTypeId}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus size={16} className="inline mr-2" />
              Add Stop
            </button>

            {gaushalas.length === 0 && (
              <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                No gaushalas found. Please ensure gaushalas are registered in the system.
              </p>
            )}
          </div>

          {/* Stops List */}
          {stops.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Added Stops ({stops.length})</h4>
              {stops
                .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
                .map((stop, index) => (
                <div key={index} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-medium text-sm">
                      {stop.sequenceOrder}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{stop.gaushalaName}</p>
                      {stop.gaushalaAddress && (
                        <p className="text-sm text-gray-600">{stop.gaushalaAddress}</p>
                      )}
                      {stop.rawMaterialTypeName && (
                        <p className="text-sm text-blue-600 font-medium">
                          Material: {stop.rawMaterialTypeName}
                        </p>
                      )}
                      {stop.expectedQuantityMinKg && stop.expectedQuantityMaxKg && (
                        <p className="text-sm text-gray-600">
                          Expected: {stop.expectedQuantityMinKg} - {stop.expectedQuantityMaxKg} kg
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStop(index)}
                    className="rounded-lg p-2 hover:bg-red-100 text-red-600"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading || stops.length === 0 || !formData.manufacturingUnitId}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? 'Creating...' : 'Create Route'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/transport/raw-material/routes')}
            className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
